import { Dialog } from 'primereact/dialog'
import React, { memo, useMemo } from 'react'
import styled from 'styled-components'
import { Message } from 'primereact/message'

import { LineChart } from '../../common/components/LineChart'
import {
    ChargingSession,
    useChargePointGetCalibrationQuery,
    useChargingSessionGetConsumptionForSessionQuery,
    ChargePoint,
    ConnectorPhaseRotations,
} from '../../common/redux/api/web-api'
import localizeDate from '../../common/utils/localizeDate'
import Text from '../../ui/Text'
import useText from '../../common/hooks/useText'

export interface IConsumptionData {
    chargingCurrent: number
    chargingCurrent2: number
    chargingCurrent3: number
    chargingVoltage: number
    chargingVoltage2: number
    chargingVoltage3: number
    timestamp: number
}

export interface ICalibration {
    currentAdjust: number
    voltageAdjust: number
}

export type ConsumptionGraphModalProps = {
    visible?: boolean
    onHide: () => void
    session?: ChargingSession
    chargePoint: ChargePoint
    connectorPhaseRotation?: ConnectorPhaseRotations
}

const SNoAvailableData = styled.div`
    height: 40px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    margin: auto;
`

export const ConsumptionGraphModal = memo(
    ({ visible, onHide, session, chargePoint, connectorPhaseRotation }: ConsumptionGraphModalProps) => {
        const text = useText()

        const { data: consumptionData, isFetching } = useChargingSessionGetConsumptionForSessionQuery(
            { id: Number(session?.id) },
            { skip: !session?.id, refetchOnMountOrArgChange: true }
        )

        const rawData = (consumptionData || []) as IConsumptionData[]

        const { data: calibrationData } = useChargePointGetCalibrationQuery(
            { chargePointId: session?.chargePointId || '' },
            { skip: !session?.chargePointId || session?.productId !== 1 }
        )

        const { currentData, voltageData } = useMemo(() => {
            const rawData = (consumptionData || []) as IConsumptionData[]

            const currentData1 = []
            const currentData2 = []
            const currentData3 = []

            const voltageData1 = []
            const voltageData2 = []
            const voltageData3 = []

            for (const item of rawData) {
                const currentAdjust = calibrationData?.currentAdjust || 1
                const voltageAdjust = calibrationData?.voltageAdjust || 1
                const x = new Date(item.timestamp).valueOf()

                currentData1.push({ y: item.chargingCurrent * currentAdjust, x })
                voltageData1.push({ y: item.chargingVoltage * voltageAdjust, x })

                currentData2.push({ y: item.chargingCurrent2 * currentAdjust, x })
                voltageData2.push({ y: item.chargingVoltage2 * voltageAdjust, x })

                currentData3.push({ y: item.chargingCurrent3 * currentAdjust, x })
                voltageData3.push({ y: item.chargingVoltage3 * voltageAdjust, x })
            }

            return {
                voltageData: {
                    datasets: [
                        {
                            label: `L1 ${text('lblVoltage')}`,
                            data: voltageData1,
                        },
                        {
                            label: `L2 ${text('lblVoltage')}`,
                            data: voltageData2,
                        },
                        {
                            label: `L3 ${text('lblVoltage')}`,
                            data: voltageData3,
                        },
                    ],
                },
                currentData: {
                    datasets: [
                        {
                            label: `L1 ${text('lblCurrent')}`,
                            data: currentData1,
                        },
                        {
                            label: `L2 ${text('lblCurrent')}`,
                            data: currentData2,
                        },
                        {
                            label: `L3 ${text('lblCurrent')}`,
                            data: currentData3,
                        },
                    ],
                },
            }
        }, [calibrationData?.currentAdjust, calibrationData?.voltageAdjust, consumptionData, text])

        const renderContent = () => {
            if (isFetching) {
                return (
                    <div className="flex flex-column gap-8 h-full">
                        <div className="shimmer-container shimmer-medium h-full" />
                        <div className="shimmer-container shimmer-medium h-full" />
                    </div>
                )
            }

            if (rawData?.length === 0) {
                return (
                    <SNoAvailableData>
                        <Text code="msgNoAvailableData" />
                    </SNoAvailableData>
                )
            }
            return (
                <>
                    <div className="flex-1">
                        <LineChart
                            yAxisLabel={text('lblCurrentAxis')}
                            xAxisLabel={text('lblTime')}
                            data={currentData}
                            className="h-full"
                        />
                    </div>
                    <div className="flex-1">
                        <LineChart
                            yAxisLabel={text('lblVoltageAxis')}
                            xAxisLabel={text('lblTime')}
                            data={voltageData}
                            className="h-full"
                        />
                    </div>
                </>
            )
        }

        return (
            <Dialog
                header={text('lblConsumptionModal')}
                visible={visible}
                onHide={onHide}
                className="lg:w-9 sm:w-full h-screen"
                pt={{
                    root: {
                        style: {
                            maxHeight: '80rem',
                            maxWidth: '90rem',
                        },
                    },
                    content: {
                        className: 'overflow-x-hidden',
                    },
                }}
            >
                <div className="flex flex-column px-2">
                    <div className="grid mb-3">
                        <div className="col-12 md:col">
                            <div className="text-2xl mb-1">
                                <Text code="lblDateInterval" />
                            </div>
                            <strong className="text-2xl font-medium">
                                {session
                                    ? `${localizeDate(session.startTime)} - ${localizeDate(session.endTime || '')}`
                                    : ''}
                            </strong>
                        </div>
                        <div className="col-12 md:col text-right">
                            <div className="text-2xl mb-1">
                                <Text code="lblTotalConsumptionKWh" />
                            </div>
                            <strong className="text-2xl font-medium">
                                {session?.totalConsumptionKwh?.toFixed(3)} kWh
                            </strong>
                        </div>
                    </div>

                    <div className="flex flex-column flex-1 justify-content-start">{renderContent()}</div>

                    {chargePoint.isOcpp && connectorPhaseRotation !== 'RST' && (
                        <Message
                            severity="info"
                            text={<Text code="lblMeterValuePhaseRotationInfoText" />}
                            className="block"
                        />
                    )}
                </div>
            </Dialog>
        )
    }
)
